import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { LoadingPanel } from '../../components/ui/Loader';
import { TextInput } from '../../components/ui/form/TextInput';
import { brand } from '../../config/brand';
import { api } from '../../lib/api';

interface Setting {
  key: string;
  label: string;
  description: string;
  /** `basis_points` or `cents`. Decides how the value is written and read. */
  type: string;
  value: number;
  default: number;
  min: number;
  max: number;
  /** Whether anybody has decided, as opposed to the default applying. */
  decided: boolean;
}

/**
 * The money settings: the split, the tip floor, the boost prices.
 *
 * ## Stored as integers, edited as money
 *
 * Everything here is a whole number in its smallest unit — basis points for the
 * split, cents for prices — because a revenue share held as `0.8` is a float in
 * somebody's arithmetic and a float that is one part in a billion off is a cent
 * that belongs to nobody. But nobody wants to type `8000` to mean eighty
 * percent, so the field shows `80` and `5.00` and converts on the way in and
 * out. The wire format never changes.
 *
 * ## It shows the consequence, not just the number
 *
 * A split is abstract until you see what it does to a payment. The panel works
 * a real $5 tip through the current rate — fee off the top, then the split —
 * because "80%" and "the host keeps $3.64 of a five dollar tip" are the same
 * fact and only one of them is possible to act on.
 */
export function AdminPaymentsPage() {
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: ['admin', 'payments'],
    queryFn: () => api.get<{ settings: Setting[] }>('/admin/payments'),
  });

  const save = useMutation({
    mutationFn: ({ key, value }: { key: string; value: number }) =>
      api.post(`/admin/payments/${key}`, { value }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'payments'] }),
  });

  if (isPending || !data) return <LoadingPanel label="Reading the settings…" />;

  const share = data.settings.find((s) => s.key === 'revenue.host_share_bp')?.value ?? 8000;

  return (
    <div className="flex flex-col gap-5" data-admin-payments>
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Payments</h1>
        <p className="mt-1 text-sm text-soft">
          What hosts keep, and what things cost. Every change is recorded in the audit log, and
          each payment stores the rate that applied when it was taken, so changing these never
          rewrites what somebody has already been paid.
        </p>
      </div>

      <Worked share={share} />

      {save.isError && (
        <Alert tone="error">That did not save. The value may be outside its allowed range.</Alert>
      )}

      <div className="flex flex-col gap-3">
        {data.settings.map((setting) => (
          <Row
            key={setting.key}
            setting={setting}
            busy={save.isPending}
            onSave={(value) => save.mutate({ key: setting.key, value })}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * One setting, with its own draft.
 *
 * Draft state per row rather than one form: these are unrelated numbers, and a
 * single Save that writes all four would make changing the tip minimum look
 * like it also confirmed the revenue split.
 */
function Row({
  setting,
  busy,
  onSave,
}: {
  setting: Setting;
  busy: boolean;
  onSave: (value: number) => void;
}) {
  const [draft, setDraft] = useState(() => forHumans(setting.value, setting.type));

  // Re-synced when the server answers, so a refetch or a rejected value snaps
  // the field back to the truth rather than leaving a number that was not saved.
  useEffect(() => {
    setDraft(forHumans(setting.value, setting.type));
  }, [setting.value, setting.type]);

  const parsed = toStored(draft, setting.type);
  const valid = parsed !== null && parsed >= setting.min && parsed <= setting.max;
  const dirty = parsed !== setting.value;

  return (
    <Card className="flex flex-col gap-3 p-5 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-sm font-semibold text-ink">{setting.label}</span>
          <code className="font-mono text-[10px] text-ghost">{setting.key}</code>
          {!setting.decided && (
            <span className="meta-caps text-faint">default · nobody has decided</span>
          )}
        </div>

        <p className="text-[13px] leading-relaxed text-soft">{setting.description}</p>

        <p className="meta-caps text-ghost">
          allowed {forHumans(setting.min, setting.type)} to {forHumans(setting.max, setting.type)}
          {' · '}default {forHumans(setting.default, setting.type)}
        </p>
      </div>

      <form
        className="flex shrink-0 items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();

          if (valid && dirty && !busy) onSave(parsed);
        }}
      >
        <span className="flex items-center gap-1.5">
          {setting.type === 'cents' && <span className="text-sm text-faint">$</span>}
          <TextInput
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            inputMode="decimal"
            aria-label={setting.label}
            invalid={draft !== '' && !valid}
            data-setting={setting.key}
            className="w-24 text-right"
          />
          {setting.type === 'basis_points' && <span className="text-sm text-faint">%</span>}
        </span>

        <Button type="submit" disabled={!valid || !dirty || busy} data-save={setting.key}>
          Save
        </Button>
      </form>
    </Card>
  );
}

/**
 * What the split actually does to a payment.
 *
 * Stripe's US card fee, applied to a $5 tip under model A: the fee comes off
 * the top, then what is left is split. Hard-coded at 2.9% + 30c because that is
 * what Stripe charges for a domestic card — an international one costs more,
 * which is the platform's problem and not the host's, and is the reason this
 * says "about".
 */
function Worked({ share }: { share: number }) {
  const gross = 500;
  const fee = Math.round(gross * 0.029) + 30;
  const net = gross - fee;
  const host = Math.floor((net * share) / 10000);
  const platform = net - host;

  return (
    <Card className="p-5">
      <p className="meta-caps mb-3 text-faint">on a $5 tip, about</p>

      <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
        <Figure label="customer pays" value={gross} />
        <Figure label="Stripe takes" value={fee} />
        <Figure label="host keeps" value={host} strong />
        <Figure label={`${brand.name} keeps`} value={platform} strong />
      </div>

      <p className="mt-3 text-[13px] leading-relaxed text-soft">
        The fee comes off the top, and what remains is split {Math.round(share / 100)}/
        {100 - Math.round(share / 100)}. So the host is never charged a processing fee they did
        not agree to, and the percentage they were promised is the percentage of what actually
        arrived.
      </p>
    </Card>
  );
}

function Figure({ label, value, strong = false }: { label: string; value: number; strong?: boolean }) {
  return (
    <span className="flex flex-col">
      <span className="meta-caps text-ghost">{label}</span>
      <span className={`tabular-nums ${strong ? 'text-base font-semibold text-ink' : 'text-sm text-soft'}`}>
        ${(value / 100).toFixed(2)}
      </span>
    </span>
  );
}

/** Stored integer to something a person would type. */
function forHumans(value: number, type: string): string {
  if (type === 'basis_points') return String(value / 100);
  if (type === 'cents') return (value / 100).toFixed(2);

  return String(value);
}

/**
 * Back again, or null if it is not a number.
 *
 * Rounded rather than truncated: `79.999` percent typed into a percent field is
 * somebody meaning 80, and `Math.round` is the only reading that does not
 * silently lose their last digit.
 */
function toStored(draft: string, type: string): number | null {
  const trimmed = draft.trim();

  if (trimmed === '' || !/^\d*\.?\d*$/.test(trimmed)) return null;

  const asNumber = Number(trimmed);

  if (!Number.isFinite(asNumber)) return null;

  if (type === 'basis_points') return Math.round(asNumber * 100);
  if (type === 'cents') return Math.round(asNumber * 100);

  return Math.round(asNumber);
}
